import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';

/** One sheet of a workbook preview, as the server renders it. */
export interface XlsxPreviewSheet {
  name: string;
  /** Display text, already formatted server-side — never raw numbers. */
  rows: string[][];
  /** The sheet's true size, which may be larger than `rows`. */
  n_rows: number;
  n_cols: number;
  truncated: boolean;
  /** Merged ranges as [r1, c1, r2, c2], 0-based. Not drawn yet. */
  merges?: number[][];
}

export interface XlsxPreviewProps {
  sheets: XlsxPreviewSheet[];
  /** Sheets left out of the preview, for a workbook too big to send whole. */
  sheetsOmitted?: number;
  /** Fill the height of its container, for the expanded window. */
  fill?: boolean;
  ariaLabel?: string;
}

/** A1-style column header from a 0-based column index (0→A, 26→AA, …). */
export function columnLetter(index: number): string {
  let s = '';
  let n = index;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

/** Right-align cells that read as numbers/currency/percent, like a spreadsheet. */
function looksNumeric(text: string): boolean {
  const t = (text || '').trim();
  if (!t) return false;
  return /\d/.test(t) && /^[-+(]?[\d.,$%)\s]+$/.test(t);
}

/**
 * A workbook as it actually looks: one grid per sheet, with the A/B/C and
 * 1/2/3 gutters, and a tab per sheet.
 *
 * It shows what the file *is*, so the columns are lettered rather than named
 * and a title row stays a row — the point is to recognise the workbook you are
 * about to download, not to read the data as a table. Every cell arrives as
 * display text; nothing is parsed or re-formatted here.
 *
 * It never fetches. Pass the sheets in, so the same grid serves a workbook
 * built in a chat and one filled by a playbook run.
 */
export function XlsxPreview({ sheets, sheetsOmitted = 0, fill, ariaLabel }: XlsxPreviewProps) {
  const theme = useTheme();
  const [active, setActive] = React.useState(0);
  if (!sheets?.length) return null;

  const sheet = sheets[Math.min(active, sheets.length - 1)];
  const rows = sheet.rows || [];
  const nCols = rows.reduce((m, r) => Math.max(m, r.length), 0);

  const border = theme.tokens.border.subtle;
  const headBg = theme.tokens.surface.overlay;
  const cellFont = theme.typography.caption.fontSize;

  const gutter: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: headBg,
    color: theme.tokens.text.tertiary,
    fontWeight: 600,
    fontSize: cellFont,
    textAlign: 'center',
    padding: '4px 8px',
    borderRight: `1px solid ${border}`,
    borderBottom: `1px solid ${border}`,
    minWidth: 36,
  };
  const colHead: React.CSSProperties = {
    ...gutter,
    position: 'sticky',
    left: undefined,
    top: 0,
    zIndex: 1,
    padding: '4px 12px',
    whiteSpace: 'nowrap',
  };

  return (
    <Box
      sx={(t) => ({
        border: `1px solid ${t.tokens.border.default}`,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
        overflow: 'hidden',
        ...(fill ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
      })}
    >
      {/* The band a spreadsheet is known by, in the product's own colour. */}
      <Box sx={(t) => ({ height: 4, bgcolor: t.tokens.brand.primary, flexShrink: 0 })} />

      {sheets.length > 1 && (
        <Box
          role="tablist"
          aria-label="Sheets"
          sx={(t) => ({
            display: 'flex',
            gap: 0.5,
            px: 1.5,
            pt: 1,
            flexWrap: 'wrap',
            flexShrink: 0,
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
          })}
        >
          {sheets.map((s, i) => {
            const selected = i === active;
            return (
              <Box
                key={`${s.name}-${i}`}
                component="button"
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActive(i)}
                title={s.name}
                sx={(t) => ({
                  ...t.typography.caption,
                  border: 'none',
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  px: 1.25,
                  py: 0.75,
                  fontWeight: selected ? 700 : 500,
                  color: selected ? t.tokens.text.primary : t.tokens.text.secondary,
                  borderBottom: `2px solid ${selected ? t.tokens.brand.primary : 'transparent'}`,
                  maxWidth: 180,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': { color: t.tokens.text.primary },
                  '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing, outline: 'none' },
                })}
              >
                {s.name}
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          overflow: 'auto',
          ...(fill ? { flex: 1, minHeight: 0 } : { maxHeight: { xs: 360, sm: 440, md: 500 } }),
        }}
      >
        <table
          aria-label={ariaLabel || `${sheet.name} sheet`}
          style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}
        >
          <thead>
            <tr>
              <th style={{ ...colHead, ...gutter, zIndex: 3, top: 0 }} />
              {Array.from({ length: nCols }).map((_, c) => (
                <th key={c} style={colHead}>
                  {columnLetter(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                <td style={gutter}>{r + 1}</td>
                {Array.from({ length: nCols }).map((_, c) => {
                  const val = row[c] ?? '';
                  return (
                    <td
                      key={c}
                      title={val}
                      style={{
                        fontSize: cellFont,
                        padding: '4px 12px',
                        borderRight: `1px solid ${border}`,
                        borderBottom: `1px solid ${border}`,
                        whiteSpace: 'nowrap',
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: looksNumeric(val) ? 'right' : 'left',
                        color: theme.tokens.text.primary,
                      }}
                    >
                      {val}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {/* A few empty rows, so the grid reads as a sheet rather than stopping dead. */}
          <tfoot aria-hidden="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <tr key={`spacer-${i}`}>
                <td style={gutter} />
                {Array.from({ length: nCols }).map((_, c) => (
                  <td
                    key={c}
                    style={{ height: 28, borderRight: `1px solid ${border}`, borderBottom: `1px solid ${border}` }}
                  />
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
      </Box>

      {/* A grid that stops at row 60 looks exactly like a grid that ends at
          row 60. Said quietly, someone takes the preview for the file — so
          when anything is cut, this is a warning, not a footnote. */}
      {(sheet.truncated || sheetsOmitted > 0) && (
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            flexShrink: 0,
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.semantic.warningSoft,
          })}
        >
          <WarningAmberRounded
            fontSize="small"
            aria-hidden
            sx={(t) => ({ color: t.tokens.semantic.warning, flexShrink: 0 })}
          />
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.warning })}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              Preview only
            </Box>
            {' — '}
            {[
              sheet.truncated
                ? `showing the first ${rows.length} rows × ${nCols} columns of ${sheet.n_rows.toLocaleString()} × ${sheet.n_cols}.`
                : '',
              sheetsOmitted > 0 ? `${sheetsOmitted} more sheet${sheetsOmitted === 1 ? '' : 's'} not shown.` : '',
              'Download for the whole workbook.',
            ]
              .filter(Boolean)
              .join(' ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
